import { useEffect } from 'react';

/**
 * Club brand colours, published as CSS variables on <html>.
 *
 * This lives here rather than inside Layout because the player profile renders
 * OUTSIDE the staff Layout — a player has no sidebar — and a player must still
 * see their own club's colours. Both callers share one implementation so the
 * variable set can never drift between the two shells.
 *
 * Two families of variable come out of here and they are NOT interchangeable:
 *
 *   --club-primary / --club-primary-N  the RAW configured hex. Use for shapes:
 *                                      fills, indicator bars, tints, borders,
 *                                      chart series. Contrast does not apply.
 *   --club-ink-dark / --club-ink-light the same hue, moved along its own
 *                                      lightness only as far as it must be to
 *                                      clear 4.5:1 against that theme's
 *                                      surfaces. Use for TEXT and icons.
 *
 * A club is free to configure any hex, and plenty of real ones are unreadable
 * as text somewhere: a yellow brand vanishes on the light theme, a navy brand
 * vanishes on the dark theme. Painting the raw hex onto text is how the sidebar
 * ended up with a ~1.2:1 "CLUB" label. The ink derivation fixes that without
 * overriding anyone's brand — see readableInk.
 */

// Parse a #rgb / #rrggbb hex into an [r,g,b] triple.
function hexToRgb(hex: string): [number, number, number] | null {
  let h = hex.trim().replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHex([r, g, b]: [number, number, number]): string {
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

// Blend an rgb triple toward a target (white/black) and return a "r g b" string
// for use in Tailwind's rgb(var(--x) / <alpha-value>) color tokens.
function mixRgb(c: [number, number, number], target: [number, number, number], t: number): string {
  const m = (a: number, b: number) => Math.round(a + (b - a) * t);
  return `${m(c[0], target[0])} ${m(c[1], target[1])} ${m(c[2], target[2])}`;
}

// ── WCAG contrast ────────────────────────────────────────────────────────────

/** WCAG 2.1 relative luminance of an sRGB triple. */
export function relativeLuminance([r, g, b]: [number, number, number]): number {
  const ch = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
}

/** WCAG contrast ratio between two sRGB triples, 1:1 … 21:1. */
export function contrastRatio(a: [number, number, number], b: [number, number, number]): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

// ── HSL round-trip (hue + saturation preserved, lightness is what we move) ────

function rgbToHsl([r, g, b]: [number, number, number]): [number, number, number] {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return [0, 0, l];
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0);
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;
  return [h / 6, s, l];
}

function hslToRgb([h, s, l]: [number, number, number]): [number, number, number] {
  if (s === 0) {
    const v = Math.round(l * 255);
    return [v, v, v];
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return [
    Math.round(channel(h + 1 / 3) * 255),
    Math.round(channel(h) * 255),
    Math.round(channel(h - 1 / 3) * 255),
  ];
}

// ── Readable ink derivation ──────────────────────────────────────────────────

/**
 * Reference surfaces the ink is guaranteed against.
 *
 * Each theme has several surfaces; we solve for the WORST one, so a single ink
 * is safe on all of them. On dark that is the LIGHTEST surface (--bg-card
 * #0d1728) because a bright ink has least headroom there; on light it is the
 * DARKEST surface (--bg-page #f1f5f9) for the mirror reason.
 */
const DARK_SURFACE: [number, number, number] = [13, 23, 40];    // #0d1728
const LIGHT_SURFACE: [number, number, number] = [241, 245, 249]; // #f1f5f9

/** WCAG AA for normal-size body text. */
const INK_TARGET = 4.5;

/**
 * Return the club colour moved only as far as it must be to clear `target`
 * against `surface`, keeping its hue and saturation.
 *
 * The first branch is the important one: a colour that ALREADY passes is
 * returned byte-identical. Most clubs' brands are readable on at least one
 * theme and are never touched there — this is a readability floor, not a
 * restyling of anyone's brand. Only the combinations that would genuinely be
 * illegible get adjusted, and they move along their own hue, so a yellow club
 * reads as gold rather than as some unrelated colour.
 */
export function readableInk(
  rgb: [number, number, number],
  surface: [number, number, number],
  target: number = INK_TARGET,
): string {
  if (contrastRatio(rgb, surface) >= target) return rgbToHex(rgb);

  const [h, s, l0] = rgbToHsl(rgb);
  // Walk AWAY from the surface: lighten on a dark surface, darken on a light one.
  const surfaceIsDark = relativeLuminance(surface) < 0.5;
  const step = surfaceIsDark ? 0.01 : -0.01;

  for (let i = 1; i <= 100; i++) {
    const l = l0 + step * i;
    if (l > 1 || l < 0) break;
    const candidate = hslToRgb([h, s, l]);
    if (contrastRatio(candidate, surface) >= target) return rgbToHex(candidate);
  }
  // Unreachable for any real surface (white always clears 4.5:1 on the dark
  // reference and black always clears it on the light one), but a hue that
  // somehow cannot get there must still yield something legible.
  return surfaceIsDark ? '#ffffff' : '#000000';
}

/**
 * Ink to put ON a club-coloured fill — the mirror of `readableInk`.
 *
 * `readableInk` answers "the brand colour is the TEXT, on our surface". This
 * answers "the brand colour is the FILL, and something must be legible on top of
 * it". Neither `--club-ink` nor `.text-on-fill` covers it: the first is for text
 * on the app's own surfaces, and the second is hardcoded `#fff`, which is exactly
 * how a yellow-brand club ended up with a 1.5:1 "New chat" button.
 *
 * White FIRST when it clears the target, because white-on-brand is the
 * conventional primary-button look and we should not restyle a club that reads
 * fine. Only when white genuinely fails do we go dark — softened `#1a1a1a`
 * rather than pure black, escalating to `#000000` for the narrow band of
 * mid-tone fills where neither extreme clears 4.5:1 on its own.
 *
 * Unlike the ink pair, this needs no per-theme variant: the fill is the club's
 * hex in both themes, so the ink on it is the same in both.
 */
const ON_FILL_CANDIDATES = ['#ffffff', '#1a1a1a', '#000000'] as const;

export function onFillInk(
  fill: [number, number, number],
  target: number = INK_TARGET,
): string {
  const ratio = (ink: string) => contrastRatio(hexToRgb(ink)!, fill);
  for (const ink of ON_FILL_CANDIDATES) {
    if (ratio(ink) >= target) return ink;
  }
  // Unreachable for any real fill — pure black or white always clears 4.5:1
  // somewhere — but never return something illegible if it ever is.
  return ON_FILL_CANDIDATES.reduce((best, ink) => (ratio(ink) > ratio(best) ? ink : best));
}

const RGB_VARS = [
  '--club-primary-rgb',
  '--club-primary-300-rgb',
  '--club-primary-400-rgb',
  '--club-primary-600-rgb',
] as const;

// Published unconditionally as a PAIR so nothing has to re-run on a theme
// change — globals.css aliases --club-ink to the right one per [data-theme].
// --club-on-primary is cleared alongside them so a previous club's ink can never
// outlive its fill; globals.css carries the default-fill value as the fallback.
const INK_VARS = ['--club-ink-dark', '--club-ink-light', '--club-on-primary'] as const;

/**
 * Imperative form — sets every club variable from the club's configured colours,
 * falling back to the platform default when the club has none.
 */
export function applyClubBranding(primary?: string | null, secondary?: string | null): void {
  const root = document.documentElement;

  if (primary) {
    root.style.setProperty('--club-primary', primary);
    // Lighter/darker variants for backgrounds and borders.
    root.style.setProperty('--club-primary-10', primary + '1a'); // 10% opacity
    root.style.setProperty('--club-primary-20', primary + '33'); // 20% opacity
    root.style.setProperty('--club-primary-40', primary + '66'); // 40% opacity
    // Wide theming: alias the design-system accent tokens to the club's primary
    // so the ENTIRE cockpit UI (buttons, active states, highlights, charts)
    // adopts the club color, not just the sidebar.
    root.style.setProperty('--c-accent', primary);
    root.style.setProperty('--c-accent-dim', `color-mix(in srgb, ${primary} 75%, black)`);
    // Remap the Tailwind `blue` palette to a shade-ramp derived from the club
    // primary, so every blue-* utility adopts the club color while keeping
    // opacity modifiers working.
    const rgb = hexToRgb(primary);
    if (rgb) {
      root.style.setProperty('--club-primary-rgb', rgb.join(' '));
      root.style.setProperty('--club-primary-300-rgb', mixRgb(rgb, [255, 255, 255], 0.45));
      root.style.setProperty('--club-primary-400-rgb', mixRgb(rgb, [255, 255, 255], 0.22));
      root.style.setProperty('--club-primary-600-rgb', mixRgb(rgb, [0, 0, 0], 0.18));
      // Contrast-corrected text variants. Both themes are solved here; CSS picks.
      root.style.setProperty('--club-ink-dark', readableInk(rgb, DARK_SURFACE));
      root.style.setProperty('--club-ink-light', readableInk(rgb, LIGHT_SURFACE));
      // Ink for anything painted ON this fill (primary buttons, filled chips).
      root.style.setProperty('--club-on-primary', onFillInk(rgb));
    } else {
      // An unparseable hex must not leave a half-applied ramp behind.
      RGB_VARS.forEach((v) => root.style.removeProperty(v));
      INK_VARS.forEach((v) => root.style.removeProperty(v));
    }
  } else {
    root.style.setProperty('--club-primary', '#3b82f6');
    root.style.setProperty('--club-primary-10', '#3b82f61a');
    root.style.setProperty('--club-primary-20', '#3b82f633');
    root.style.setProperty('--club-primary-40', '#3b82f666');
    // No club color → fall back to the default theme accent from globals.css
    // and the default Tailwind blue palette (config defaults). Leaving the ink
    // vars unset is deliberate: globals.css then falls back to the Argus violet,
    // which is what an unbranded install should look like.
    root.style.removeProperty('--c-accent');
    root.style.removeProperty('--c-accent-dim');
    RGB_VARS.forEach((v) => root.style.removeProperty(v));
    INK_VARS.forEach((v) => root.style.removeProperty(v));
  }

  if (secondary) {
    root.style.setProperty('--club-secondary', secondary);
    root.style.setProperty('--club-secondary-40', secondary + '66'); // 40% opacity
    // accent-warm drives gradients (primary → warm); pair it with secondary.
    root.style.setProperty('--c-accent-warm', secondary);
  } else {
    root.style.setProperty('--club-secondary', '#22c55e');
    root.style.setProperty('--club-secondary-40', '#22c55e66');
    // Without a secondary, keep the gradient on-brand by warming the primary.
    if (primary) {
      root.style.setProperty('--c-accent-warm', `color-mix(in srgb, ${primary} 85%, black)`);
    } else {
      root.style.removeProperty('--c-accent-warm');
    }
  }
}

/** React form — re-applies whenever the club's configured colours change. */
export function useClubBranding(primary?: string | null, secondary?: string | null): void {
  useEffect(() => {
    applyClubBranding(primary, secondary);
  }, [primary, secondary]);
}

/** Exported for tests only — the surfaces the inks are guaranteed against. */
export const INK_SURFACES = { dark: DARK_SURFACE, light: LIGHT_SURFACE, target: INK_TARGET } as const;
